import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { AUTH_TOKEN_KEY } from "@/lib/constants";
import { toast } from "sonner";
import { Send, Bot, User, AlertCircle, Crown } from "lucide-react";
import { Link } from "react-router-dom";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [quota, setQuota] = useState(null);
  const [limitError, setLimitError] = useState(null);
  const scrollRef = useRef();

  useEffect(() => {
    api.get("/chat/history").then(r => setMessages(r.data));
    api.get("/chat/quota").then(r => setQuota(r.data));
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, streamingText]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const q = input.trim();
    setInput("");
    setLimitError(null);
    const userId = "u"+Date.now();
    setMessages(prev => [...prev, { role: "user", content: q, id: userId }]);
    setStreaming(true);
    setStreamingText("");

    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: q }),
      });
      if (res.status === 429) {
        const body = await res.json();
        setLimitError(body.detail);
        setStreaming(false);
        setMessages(prev => prev.filter(m => m.id !== userId));
        return;
      }
      if (!res.ok) throw new Error("Stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE events line by line
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.type === "delta") {
              full += payload.content;
              setStreamingText(full);
            } else if (payload.type === "error") {
              toast.error(payload.error || "Stream error");
            }
          } catch {}
        }
      }
      // Commit the assistant reply
      setMessages(prev => [...prev, { role: "assistant", content: full, id: "a"+Date.now() }]);
      setStreamingText("");
      // Refresh quota
      api.get("/chat/quota").then(r => setQuota(r.data));
    } catch (err) {
      toast.error("Stream failed");
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    "Am I being paid fairly?",
    "How do I negotiate a $20K raise?",
    "Should I quit my job?",
    "What skills should I learn next?",
  ];

  return (
    <div className="p-10 max-w-4xl h-screen flex flex-col" data-testid="chat-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 04</div>
          <h1 className="font-display font-bold text-4xl tracking-tight">Career Advisor</h1>
          <p className="text-muted-foreground mt-2 text-sm">Claude 4.5 · Streaming · Remembers your conversation</p>
        </div>
        {quota && !quota.unlimited && (
          <div className="text-right" data-testid="chat-quota">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Today</div>
            <div className="font-display font-bold text-xl">{quota.used} / {quota.limit}</div>
            <div className="text-[10px] text-muted-foreground font-mono">Free tier</div>
          </div>
        )}
        {quota?.unlimited && (
          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono uppercase tracking-widest" data-testid="chat-unlimited">
            <Crown size={12}/> Pro · Unlimited
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-2" data-testid="chat-messages">
        {messages.length === 0 && !streaming && (
          <div className="border border-border p-6" data-testid="chat-empty">
            <div className="font-display font-bold text-lg mb-3">Ask me anything about your career.</div>
            <div className="grid md:grid-cols-2 gap-2">
              {suggestions.map((s, i) => (
                <button key={s} data-testid={`suggestion-${i}`} onClick={()=>setInput(s)} className="text-left border border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-foreground">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex gap-3 ${m.role==="user"?"justify-end":""}`} data-testid={`msg-${m.role}`}>
            {m.role==="assistant" && <div className="w-8 h-8 bg-secondary border border-border flex items-center justify-center shrink-0"><Bot size={14} className="text-primary"/></div>}
            <div className={`max-w-[80%] p-4 ${m.role==="user"?"bubble-user":"bubble-ai"} whitespace-pre-wrap text-sm leading-relaxed`}>{m.content}</div>
            {m.role==="user" && <div className="w-8 h-8 bg-primary flex items-center justify-center shrink-0"><User size={14}/></div>}
          </div>
        ))}
        {streaming && (
          <div className="flex gap-3" data-testid="chat-streaming">
            <div className="w-8 h-8 bg-secondary border border-border flex items-center justify-center"><Bot size={14} className="text-primary"/></div>
            <div className="bubble-ai p-4 text-sm whitespace-pre-wrap">
              {streamingText || <span className="text-muted-foreground">Thinking...</span>}
              <span className="inline-block w-2 h-4 bg-primary ml-1 animate-pulse"/>
            </div>
          </div>
        )}
      </div>

      {limitError && (
        <div className="mt-4 border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="chat-limit-warning">
          <AlertCircle size={16} className="text-amber-400 mt-0.5"/>
          <div className="flex-1 text-sm text-amber-100">
            {limitError} <Link to="/app/pricing" className="underline font-bold ml-1">Upgrade to Pro →</Link>
          </div>
        </div>
      )}

      <div className="pt-4 flex gap-2">
        <input data-testid="chat-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter") send();}} disabled={streaming} placeholder="Ask about salary, career moves, negotiation..." className="flex-1 bg-card border border-border px-4 py-3 focus:outline-none focus:border-primary disabled:opacity-50" />
        <button data-testid="chat-send" onClick={send} disabled={streaming} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-5 disabled:opacity-50"><Send size={16}/></button>
      </div>
    </div>
  );
}
